import { FaroTableBulkActions } from "@components/common/faro-table/faro-table-bulk-actions";
import { SnapshotBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { SkeletonProjectListItemCard } from "@components/common/mobile/skeleton-project-list-item-card";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { Snapshot } from "@custom-types/base-types";
import { Box, Divider } from "@mui/material";
import { useSnapshotMenuOptions } from "@pages/project-details/project-snapshots/hooks/use-snapshot-menu-options";
import { SnapshotListItemCardMobile } from "@pages/project-details/project-snapshots/snapshot-list-item-card-mobile";
import { SnapshotsBeingCreatedIndicator } from "@pages/project-details/project-snapshots/snapshots-being-created-indicator";
import { SnapshotsSortBy } from "@pages/project-details/project-snapshots/snapshots-table-types";
import {
  getOpenSnapshotHref,
  trackOpenSnapshot,
} from "@pages/project-details/project-snapshots/snapshot-utils";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { setTableType } from "@store/table/table-slice";
import { TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect } from "react";
import { CreateSnapshot } from "@pages/project-details/project-snapshots/create-snapshot";
import { selectedProjectIdSelector } from "@store/projects/projects-selector";
import { FaroTableEmptyPage } from "@components/common/faro-table/faro-table-empty-page";

interface Props {
  /** The snapshots that are shown in the list */
  snapshots: Snapshot[];

  /** Whether the data for showing the list is loading or not */
  isLoading: boolean;

  /** Snapshots sort criteria */
  sortBy: SnapshotsSortBy;

  /** List of all the bulk actions and callbacks */
  bulkActionButtons: SnapshotBulkButtonComponents;
}

/**
 * Mobile-specific view for the snapshots list with card layout and bulk actions footer.
 */
export function SnapshotsMobileView({
  snapshots,
  isLoading,
  sortBy,
  bulkActionButtons,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { trackAsyncEvent } = useTrackEvent();
  const menuItems = useSnapshotMenuOptions();
  const selectedEntities = useAppSelector(
    selectedEntitiesSelector("snapshots")
  );
  const projectLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const selectedProjectId = useAppSelector(selectedProjectIdSelector);

  const shouldShowEmptyPage = snapshots.length === 0 && !isLoading;

  // Make sure the correct table type is set in the store so bulk actions operate on snapshots.
  useEffect(() => {
    dispatch(setTableType(TableType.snapshots));
  }, [dispatch]);

  const handleSnapshotClick = useCallback(
    async (snapshot: Snapshot) => {
      const openTarget = projectLaunchSettings.defaultOpenProjectTarget;

      await trackOpenSnapshot({
        openTarget,
        trackAsyncEvent,
        openFrom: "snapshot-table-row",
        accessLevel: snapshot.accessLevel,
        createdAt: snapshot.createdAt,
      });

      const targetHref = getOpenSnapshotHref({
        openTarget,
        snapshotId: snapshot.id,
      });

      window.open(targetHref, "_blank", "noopener,noreferrer");
    },
    [projectLaunchSettings.defaultOpenProjectTarget, trackAsyncEvent]
  );

  const isAnyRowSelected = selectedEntities.length !== 0;
  const allSnapshotIds = snapshots.map((snapshot) => snapshot.id);

  if (shouldShowEmptyPage) {
    return <FaroTableEmptyPage />;
  }

  return (
    <>
      <SnapshotsBeingCreatedIndicator sx={{ px: 2, py: 1 }} />
      <VirtualizedGrid
        // The `key` here forces React to remount the grid whenever `sortBy` changes.
        // This ensures that the scroll position resets and the grid re-renders from the first item
        key={sortBy}
        items={snapshots}
        isLoading={isLoading}
        renderSkeletonItem={(index) => (
          <Box key={index}>
            <SkeletonProjectListItemCard />
          </Box>
        )}
        estimateSize={80}
        renderItem={(snapshot, index) => (
          <Box key={snapshot.id}>
            {index !== 0 && <Divider />}

            <SnapshotListItemCardMobile
              snapshot={snapshot}
              onCardClick={() => void handleSnapshotClick(snapshot)}
              menuItems={menuItems}
            />
          </Box>
        )}
        shouldRenderFooterOutside={isAnyRowSelected}
        renderFooterAction={() => (
          <>
            <FaroTableBulkActions
              bulkActionsButtons={bulkActionButtons}
              numberOfSelectedRows={selectedEntities.length}
              isLoading={isLoading}
              allRowIds={allSnapshotIds}
            />
            {!isAnyRowSelected && selectedProjectId && (
              <CreateSnapshot projectId={selectedProjectId} />
            )}
          </>
        )}
      />
    </>
  );
}
